import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/Bits";

export const FEATURES = [
  ["library", "Templates & videos"],
  ["ask", "Ask Lindsay (questions)"],
  ["seasonal", "Seasonal ideas"],
];

const Stars = ({ value, onChange, testId }) => (
  <div className="flex gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button type="button" key={n} onClick={() => onChange(n)} data-testid={`${testId}-${n}`} aria-label={`${n} stars`}>
        <Star className={`h-6 w-6 transition-colors ${n <= value ? "fill-sun text-sun" : "text-border"}`} />
      </button>
    ))}
  </div>
);

const Pills = ({ options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([v, l]) => (
      <button type="button" key={v} onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
        className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${value === v ? "bg-navy text-white border-navy" : "border-border hover:border-navy"}`}>{l}</button>
    ))}
  </div>
);

const Q = ({ n, title, children }) => (
  <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
    <h3 className="text-lg text-navy"><span className="font-mono text-xs text-pinkb mr-2">0{n}</span>{title}</h3>
    {children}
  </div>
);

export default function Survey() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [f, setF] = useState({ ratings: {}, feedback: {}, missing: "", nps: null, comments: "", testimonial_ok: false });
  useEffect(() => { api.get("/survey/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const [photo, setPhoto] = useState(null);

  const submit = async () => {
    if (FEATURES.some(([k]) => !f.ratings[k]) || !(f.most_valuable || "").trim() || f.nps === null) return toast.error("Please answer every question with a rating.");
    try {
      await api.post("/survey", f);
      if (f.testimonial_ok && photo) {
        const fd = new FormData();
        fd.append("file", photo);
        await api.post("/survey/photo", fd);
      }
      toast.success("Thank you! Your feedback really helps.");
      navigate("/app");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-3xl space-y-5">
      <PageHeader eyebrow={`Testing phase · ${status?.month || ""}`} title="Beta Feedback — Help Us Improve The Roots Club" />
      {status?.submitted && <p className="text-sm text-emerald-700" data-testid="survey-already-submitted">You've already given feedback this month. Sending again will update your answers.</p>}
      <Q n={1} title="Rate each part of the club, and tell Lindsay what it's done for you">
        {FEATURES.map(([k, label]) => (
          <div key={k} className="space-y-2 border-t border-border pt-4 first:border-0 first:pt-0">
            <div className="flex flex-wrap items-center justify-between gap-3"><span className="font-medium">{label}</span>
              <Stars value={f.ratings[k] || 0} onChange={(v) => set("ratings", { ...f.ratings, [k]: v })} testId={`survey-rating-${k}`} /></div>
            <Textarea rows={2} value={f.feedback[k] || ""} onChange={(e) => set("feedback", { ...f.feedback, [k]: e.target.value })}
              placeholder="What has it helped you do? A sentence or two in your own words." data-testid={`survey-feedback-${k}`} className="bg-white" />
          </div>
        ))}
      </Q>
      <Q n={2} title="What's the most valuable thing you've found in The Roots Club so far?">
        <Textarea rows={3} value={f.most_valuable || ""} onChange={(e) => set("most_valuable", e.target.value)} data-testid="survey-most-valuable" className="bg-white" />
      </Q>
      <Q n={3} title="What would make this even better for you?">
        <Textarea rows={3} value={f.missing} onChange={(e) => set("missing", e.target.value)} data-testid="survey-missing" className="bg-white" />
      </Q>
      <Q n={4} title="Would you recommend The Roots Club to another founder or operator? (0-10)">
        <div className="flex flex-wrap gap-1.5">
          {[...Array(11).keys()].map((n) => (
            <button type="button" key={n} onClick={() => set("nps", n)} data-testid={`survey-nps-${n}`}
              className={`h-10 w-10 rounded-lg border text-sm transition-colors ${f.nps === n ? "bg-pinkb text-white border-pinkb" : "border-border hover:border-pinkb"}`}>{n}</button>
          ))}
        </div>
        <Textarea rows={2} value={f.recommend_why || ""} onChange={(e) => set("recommend_why", e.target.value)} placeholder="Why, or why not?" data-testid="survey-recommend-why" className="bg-white mt-3" />
      </Q>
      <Q n={5} title="Anything else?">
        <Textarea rows={3} value={f.comments} onChange={(e) => set("comments", e.target.value)} data-testid="survey-comments" className="bg-white" />
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox checked={f.testimonial_ok} onCheckedChange={(v) => set("testimonial_ok", !!v)} data-testid="survey-testimonial-ok" className="mt-0.5" />
          Lindsay can quote my feedback as a testimonial, with my first name and restaurant name.
        </label>
        {f.testimonial_ok && (
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground" htmlFor="survey-photo">Add a photo of your restaurant to show with your testimonial (optional)</label>
            <input id="survey-photo" type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setPhoto(e.target.files[0] || null)} data-testid="survey-photo-input" className="block text-sm" />
          </div>
        )}
      </Q>
      <Button onClick={submit} data-testid="survey-submit-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white px-8">Send my feedback</Button>
    </div>
  );
}
